
import Vue, { PropType, VNode, CreateElement } from 'vue'

interface SnapSliderAutoplay {
  delay: number
  disableOnInteraction?: boolean
}

interface SnapSliderNavigation {
  nextEl?: string
  prevEl?: string
}

interface SnapSliderOptions {
  slideWidth?: number
  slideGap?: number
  speed?: number
  loop?: boolean
  autoplay?: SnapSliderAutoplay | false
  navigation?: SnapSliderNavigation | false
}

export default Vue.extend({
  name: 'SnapSlider',
  props: {
    options: {
      type: Object as PropType<SnapSliderOptions>,
      default(): SnapSliderOptions {
        return {}
      }
    }
  },
  data() {
    return {
      currentIndex: 0,
      isTransitioning: false,
      autoplayTimer: null as number | null,
      viewWidth: 0,
      touchStartX: 0,
      touchStartY: 0,
      touchDeltaX: 0,
      isSwiping: false
    }
  },
  computed: {
    slideVNodes(): VNode[] {
      const slots = this.$slots.default || []
      return slots.filter(function (vnode: VNode) {
        return !!vnode.tag
      })
    },
    slideCount(): number {
      return this.slideVNodes.length
    },
    slideWidth(): number {
      return (this.options && this.options.slideWidth) || this.viewWidth
    },
    slideGap(): number {
      return (this.options && this.options.slideGap) || 24
    },
    slideTotal(): number {
      return this.slideWidth + this.slideGap
    },
    speed(): number {
      return (this.options && this.options.speed) || 1000
    },
    isLoop(): boolean {
      if (this.options && this.options.loop !== undefined) {
        return this.options.loop
      }
      return true
    },
    autoplayConfig(): SnapSliderAutoplay | false {
      if (this.options && this.options.autoplay !== undefined) {
        return this.options.autoplay
      }
      return false
    },
    showNavigation(): boolean {
      if (this.options && this.options.navigation !== undefined) {
        return this.options.navigation !== false
      }
      return true
    },
    isFullWidth(): boolean {
      return !this.options || !this.options.slideWidth
    },
    cloneCount(): number {
      return this.isFullWidth ? 1 : 2
    },
    trackStyle(): Record<string, string> {
      const style: Record<string, string> = {}
      const swipePx = this.isSwiping ? this.touchDeltaX : 0
      if (this.isFullWidth) {
        const basePercent = -this.currentIndex * 100
        style.transform = 'translateX(calc(' + basePercent + '% + ' + swipePx + 'px))'
      } else {
        const offset = this.currentIndex * this.slideTotal
        const viewCenter = this.viewWidth / 2
        const slideCenter = this.slideWidth / 2
        style.transform = 'translateX(' + (viewCenter - slideCenter - offset + swipePx) + 'px)'
      }
      if (this.isTransitioning) {
        style.transition = 'transform ' + this.speed + 'ms ease'
      } else {
        style.transition = 'none'
      }
      if (!this.isFullWidth && this.viewWidth === 0) {
        style.visibility = 'hidden'
      }
      if (!this.isFullWidth && this.slideGap > 0) {
        style.gap = this.slideGap + 'px'
      }
      return style
    }
  },
  mounted() {
    this.measureWidth()
    if (this.isLoop && this.slideCount > 0) {
      this.currentIndex = this.cloneCount
    }
    window.addEventListener('resize', this.onResize)
    this.$el.addEventListener('touchmove', this.onTouchMove as EventListener, { passive: false })
    if (this.autoplayConfig) {
      this.startAutoplay()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.$el.removeEventListener('touchmove', this.onTouchMove as EventListener)
    this.stopAutoplay()
  },
  methods: {
    /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion */
    cloneVNode(vnode: VNode, h: CreateElement, suffix: string): VNode | string {
      const self = this

      // Text node
      if (!vnode.tag && !vnode.componentOptions) {
        return vnode.text || ''
      }

      // Component VNode
      if (vnode.componentOptions) {
        const opts = vnode.componentOptions
        const origData: any = vnode.data || {}
        const data: Record<string, any> = {}

        if (origData.staticClass) data.staticClass = origData.staticClass
        if (origData.class) data.class = origData.class
        if (origData.style) data.style = origData.style
        if (origData.staticStyle) data.staticStyle = origData.staticStyle
        if (origData.attrs) data.attrs = Object.assign({}, origData.attrs)
        if (origData.domProps) data.domProps = Object.assign({}, origData.domProps)
        if (origData.slot) data.slot = origData.slot
        if (origData.on) data.nativeOn = Object.assign({}, origData.on)
        if (origData.directives) data.directives = origData.directives.slice()

        data.props = Object.assign({}, opts.propsData)
        if (opts.listeners) data.on = Object.assign({}, opts.listeners)
        data.key = (origData.key != null ? String(origData.key) : '') + '-' + suffix

        const children = opts.children
          ? opts.children.map(function (c: VNode) {
            return self.cloneVNode(c, h, suffix)
          })
          : undefined

        return h(opts.Ctor as any, data, children as any)
      }

      // Element VNode
      const origElemData: any = vnode.data || {}
      const elemData: Record<string, any> = {}

      if (origElemData.staticClass) elemData.staticClass = origElemData.staticClass
      if (origElemData.class) elemData.class = origElemData.class
      if (origElemData.style) elemData.style = origElemData.style
      if (origElemData.staticStyle) elemData.staticStyle = origElemData.staticStyle
      if (origElemData.attrs) elemData.attrs = Object.assign({}, origElemData.attrs)
      if (origElemData.domProps) elemData.domProps = Object.assign({}, origElemData.domProps)
      if (origElemData.on) elemData.on = Object.assign({}, origElemData.on)
      if (origElemData.nativeOn) elemData.nativeOn = Object.assign({}, origElemData.nativeOn)
      if (origElemData.directives) elemData.directives = origElemData.directives.slice()
      if (origElemData.slot) elemData.slot = origElemData.slot
      elemData.key = (origElemData.key != null ? String(origElemData.key) : vnode.tag) + '-' + suffix

      const children = vnode.children
        ? vnode.children.map(function (c: VNode) {
          return self.cloneVNode(c, h, suffix)
        })
        : undefined

      return h(vnode.tag!, elemData, children as any)
    },
    /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion */
    onResize() {
      this.measureWidth()
    },
    measureWidth() {
      const el = this.$el as HTMLElement
      if (el) {
        this.viewWidth = el.offsetWidth
      }
    },
    slideNext() {
      if (this.isTransitioning) return
      if (!this.isLoop && this.currentIndex >= this.slideCount - 1) return
      this.isTransitioning = true
      this.currentIndex = this.currentIndex + 1
    },
    slidePrev() {
      if (this.isTransitioning) return
      if (!this.isLoop && this.currentIndex <= 0) return
      this.isTransitioning = true
      this.currentIndex = this.currentIndex - 1
    },
    handleUserInteraction() {
      const config = this.autoplayConfig
      if (!config) return
      if (config.disableOnInteraction) {
        this.stopAutoplay()
      } else {
        this.resetAutoplay()
      }
    },
    onTransitionEnd(event: TransitionEvent) {
      const track = this.$refs.track as HTMLElement
      if (event.target !== track || event.propertyName !== 'transform') return
      this.isTransitioning = false
      if (!this.isLoop) return
      const len = this.slideCount
      const cc = this.cloneCount
      if (this.currentIndex >= len + cc) {
        this.currentIndex = cc
      } else if (this.currentIndex <= cc - 1) {
        this.currentIndex = len + cc - 1
      }
    },
    startAutoplay() {
      const self = this
      const config = this.autoplayConfig
      if (!config) return
      if (this.autoplayTimer) return
      this.autoplayTimer = window.setInterval(function () {
        self.slideNext()
      }, config.delay)
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer)
        this.autoplayTimer = null
      }
    },
    resetAutoplay() {
      if (!this.autoplayConfig) return
      this.stopAutoplay()
      this.startAutoplay()
    },
    onTouchStart(event: TouchEvent) {
      if (this.isTransitioning) return
      const touch = event.touches[0]
      this.touchStartX = touch.clientX
      this.touchStartY = touch.clientY
      this.touchDeltaX = 0
      this.isSwiping = false
    },
    onTouchMove(event: TouchEvent) {
      const touch = event.touches[0]
      const dx = touch.clientX - this.touchStartX
      const dy = touch.clientY - this.touchStartY
      // 初回判定: 水平方向の移動が大きければスワイプとみなす
      if (!this.isSwiping) {
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return
        if (Math.abs(dy) > Math.abs(dx)) return
        this.isSwiping = true
        this.stopAutoplay()
      }
      if (this.isSwiping) {
        event.preventDefault()
        this.touchDeltaX = dx
      }
    },
    onTouchEnd() {
      if (!this.isSwiping) return
      const threshold = (this.isFullWidth ? this.viewWidth : this.slideWidth) * 0.2
      if (this.touchDeltaX < -threshold) {
        this.slideNext()
      } else if (this.touchDeltaX > threshold) {
        this.slidePrev()
      }
      this.isSwiping = false
      this.touchDeltaX = 0
      this.handleUserInteraction()
    }
  },
  render(h: CreateElement): VNode {
    const self = this
    const slides = this.slideVNodes
    let trackChildren: Array<VNode | string> = slides.slice()

    if (this.isLoop && slides.length > 0) {
      const cc = this.cloneCount
      const len = slides.length
      const prefixClones: Array<VNode | string> = []
      const suffixClones: Array<VNode | string> = []
      for (let i = cc; i > 0; i--) {
        const prefixIdx = (((len - i) % len) + len) % len
        prefixClones.push(this.cloneVNode(slides[prefixIdx], h, 'clone-pre-' + i))
      }
      for (let i = 0; i < cc; i++) {
        const suffixIdx = i % len
        suffixClones.push(this.cloneVNode(slides[suffixIdx], h, 'clone-suf-' + i))
      }
      trackChildren = prefixClones.concat(slides).concat(suffixClones)
    }

    const track = h(
      'div',
      {
        ref: 'track',
        staticClass: 'snap-slider__track',
        style: this.trackStyle,
        on: { transitionend: this.onTransitionEnd }
      },
      trackChildren as VNode[]
    )

    const children: VNode[] = [track]

    if (this.showNavigation) {
      children.push(
        h(
          'div',
          {
            staticClass: 'snap-slider__prev',
            on: {
              click() {
                self.slidePrev()
                self.handleUserInteraction()
              }
            }
          },
          [h('span')]
        ),
        h(
          'div',
          {
            staticClass: 'snap-slider__next',
            on: {
              click() {
                self.slideNext()
                self.handleUserInteraction()
              }
            }
          },
          [h('span')]
        )
      )
    }

    return h(
      'div',
      {
        staticClass: 'snap-slider',
        on: {
          touchstart: self.onTouchStart,
          touchend: self.onTouchEnd
        }
      },
      children
    )
  }
})
